import { z } from 'zod';

export const RELEASE_ENVIRONMENTS = ['dev', 'sit', 'uat', 'prd'] as const;
export const ReleaseEnvironmentSchema = z.enum(RELEASE_ENVIRONMENTS);
export type ReleaseEnvironment = z.infer<typeof ReleaseEnvironmentSchema>;

export const RELEASE_ADAPTERS = ['mobile_app', 'web', 'server', 'desktop_app'] as const;
export const ReleaseAdapterSchema = z.enum(RELEASE_ADAPTERS);
export type ReleaseAdapter = z.infer<typeof ReleaseAdapterSchema>;

export const RELEASE_ADAPTER_LABELS: Record<ReleaseAdapter, string> = {
  mobile_app: 'Mobile App Release',
  web: 'Web Release',
  server: 'Server Release',
  desktop_app: 'Desktop App Release',
};

export interface AdapterWorkflowTemplate {
  /** Workflow file basename without extension — matches both `.yml` and `.yaml`. */
  stem: string;
  /** Human title shown instead of the raw filename. */
  title: string;
}

/**
 * Release workflows each adapter supports (UCH-256): only these are selectable
 * on a draft, displayed by title. `environment` is injected as a system input,
 * so a single file per adapter serves every environment. Mobile ships a store
 * build plus an adhoc (Firebase App Distribution) build.
 */
export const ADAPTER_WORKFLOW_TEMPLATES: Record<
  ReleaseAdapter,
  readonly AdapterWorkflowTemplate[]
> = {
  mobile_app: [
    { stem: 'release-mobile', title: 'Release to Store' },
    { stem: 'release-mobile-adhoc', title: 'Release to Firebase App Distributions' },
  ],
  desktop_app: [{ stem: 'release-desktop', title: 'Release Desktop App' }],
  server: [{ stem: 'release-server', title: 'Release Server' }],
  web: [{ stem: 'release-web', title: 'Release Web' }],
};

/** Workflow file basename without its `.yml`/`.yaml` extension. */
export const workflowStem = (file: string): string => file.replace(/\.ya?ml$/i, '');

/** The adapter template matching a workflow file (by stem), or null when unsupported. */
export const adapterWorkflowFor = (
  adapter: ReleaseAdapter,
  file: string,
): AdapterWorkflowTemplate | null =>
  ADAPTER_WORKFLOW_TEMPLATES[adapter].find((t) => t.stem === workflowStem(file)) ?? null;

export const VERSION_PATTERN = /^\d+\.\d+\.\d+$/;
export const VersionStringSchema = z
  .string()
  .regex(VERSION_PATTERN, 'must be MAJOR.MINOR.PATCH (e.g. 1.4.2)');

export const COMMIT_SHA_PATTERN = /^[0-9a-f]{40}$/;
export const CommitShaSchema = z
  .string()
  .regex(COMMIT_SHA_PATTERN, 'must be a full 40-char lowercase commit SHA');

/** Per-workflow input map, mirrored from releases/shapes to avoid an import cycle. */
export const ChannelWorkflowInputsSchema = z.record(z.record(z.string()));

/** Mirrored release-status enum (kept local to avoid a cycle with releases/shapes). */
export const ChannelReleaseStatusSchema = z.enum([
  'draft',
  'running',
  'succeeded',
  'failed',
  'publishing',
  'published',
]);

// Mirrored from releases/shapes WORKFLOW_FILE_PATTERN (import cycle).
const DeployWorkflowFileSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9._-]+\.(yml|yaml)$/, 'must be a .yml/.yaml workflow filename');

/**
 * Publish = deploy (0012): where Publish deploys a channel's build before it
 * is marked published. `gitops` dispatches the shared set-image workflow in a
 * GitOps repo (images tagged v{version}-{env}.{build}), optionally followed by
 * an ArgoCD sync; `workflow` dispatches a publish workflow in the product repo.
 */
export const DeployConfigSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('gitops'),
      repo: z
        .string()
        .trim()
        .regex(/^[\w.-]+\/[\w.-]+$/, 'must be owner/name'),
      /** Branch the workflow is dispatched on (the GitOps repo's default branch). */
      ref: z.string().trim().min(1).max(200),
      workflowFile: DeployWorkflowFileSchema,
      /** set-image.yml's own guard: lowercase, digits and dashes. */
      overlay: z
        .string()
        .trim()
        .regex(/^[a-z0-9-]+$/, 'must be an overlay directory name'),
      registry: z
        .string()
        .trim()
        .regex(/^[\w.-]+(:\d+)?$/, 'must be a registry host'),
      /** Kustomize image name → repository under the registry. */
      images: z
        .record(
          z
            .string()
            .trim()
            .regex(/^[\w./-]+$/, 'invalid image name'),
          z
            .string()
            .trim()
            .regex(/^[\w./-]+$/, 'invalid repository'),
        )
        .refine((m) => Object.keys(m).length > 0, 'at least one image is required'),
      argocd: z
        .object({
          url: z.string().trim().url().startsWith('https://', 'must be https'),
          appName: z
            .string()
            .trim()
            .regex(/^[\w.-]+$/, 'invalid ArgoCD app name'),
        })
        .strict()
        .optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('workflow'),
      workflowFile: DeployWorkflowFileSchema,
      /** Build workflow whose GitHub run id is passed as `buildRunId`. */
      buildWorkflowFile: DeployWorkflowFileSchema,
    })
    .strict(),
]);
export type DeployConfig = z.infer<typeof DeployConfigSchema>;

export const ReleaseChannelSchema = z.object({
  environment: ReleaseEnvironmentSchema,
  /** Highest published version for this product+environment; null until first publish. */
  versionFloor: z.string().nullable(),
  tauriPackageId: z.string(),
  /** Public CDN URL of this channel's latest.json update pointer; null until storage is configured or no tauriPackageId. */
  updateManifestUrl: z.string().nullable(),
  /** Stable latest-installer link base (`{downloads CDN}/latest/{env}/{packageId}`); null until the downloads CDN is configured or no tauriPackageId. */
  latestDownloadUrlBase: z.string().nullable(),
  /** Firebase App Distribution app ids per platform (1:<projectNumber>:<platform>:<hash>); '' when not configured. */
  firebaseAppId: z.string(), // Android
  firebaseAppIdIos: z.string(),
  /** Default tester group aliases for Publish to testers. */
  fadTesterGroups: z.array(z.string()),
  /** Slack channel id for build notifications; '' when not configured. */
  slackChannelId: z.string(),
  /** Last-saved workflow selection for this channel; a new draft pre-fills from it. */
  defaultWorkflows: z.array(z.string()),
  defaultWorkflowInputs: ChannelWorkflowInputsSchema,
  /** Publish deploy target; null = Publish only marks the release published. */
  deployConfig: DeployConfigSchema.nullable(),
  /** An ArgoCD token is stored (write-only; the value never leaves the server). */
  hasDeploySecret: z.boolean(),
  /** Most recent release in this channel (any status); null when none exist. */
  latestRelease: z
    .object({
      id: z.string().uuid(),
      version: z.string(),
      buildNumber: z.number().int(),
      status: ChannelReleaseStatusSchema,
      createdAt: z.string(),
    })
    .nullable(),
});
export type ReleaseChannelInfo = z.infer<typeof ReleaseChannelSchema>;

export const ReleaseProductSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  repoOwner: z.string(),
  repoName: z.string(),
  adapter: ReleaseAdapterSchema,
  /** Next build number to be reserved — one monotonic sequence per product across all environments. */
  nextBuildNumber: z.number().int(),
  /** Soft-disabled timestamp — hidden from release surfaces until re-enabled; null = active. */
  disabledAt: z.string().nullable(),
  /** Branches listed first in the ref picker's Branch tab (e.g. dev, sit, uat, prd). */
  pinnedBranches: z.array(z.string()),
  channels: z.array(ReleaseChannelSchema),
});
export type ReleaseProductInfo = z.infer<typeof ReleaseProductSchema>;
